import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, setDoc, serverTimestamp, orderBy, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../AuthContext';
import { Link } from 'react-router-dom';
import './ServicesPage.css';
import LoadingSpinner from '../components/LoadingSpinner';
import EditableContentMultilang from '../components/EditableContentMultilang';
import { useTranslation } from 'react-i18next';

// --- Componente per Input Multilingua ---
const MultilangInput = ({ label, field, values, onChange, isTextarea = false, rows = 3 }) => {
    const languages = ['it', 'en', 'de', 'nl', 'fr'];
    return (
        <div className="form-group">
            <label>{label}</label>
            <div className="multilang-input-grid">
                {languages.map(lang => ( isTextarea ? ( <textarea key={lang} value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} rows={rows} className="multilang-input"/> ) : ( <input key={lang} type="text" value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} className="multilang-input"/> ) ))}
            </div>
        </div>
    );
};

// --- Componente per il selettore di immagini ---
const ImageSelectorModal = ({ uploadedImages, onSelect, onCancel }) => {
    return (
        <div className="image-selector-modal-overlay" onClick={onCancel}>
            <div className="image-selector-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Scegli un'immagine caricata</h3>
                {uploadedImages.length === 0 ? ( <p>Nessuna immagine pre-caricata.</p> ) : (
                    <div className="image-selector-grid">
                        {uploadedImages.map((url, index) => ( <div key={index} className="image-selector-item" onClick={() => onSelect(url)}><img src={url} alt={`Immagine caricata ${index + 1}`} /></div> ))}
                    </div>
                )}
                <button type="button" className="cancel-button" onClick={onCancel}>Chiudi</button>
            </div>
        </div>
    );
};

// --- Componente per gestire un singolo blocco di contenuto ---
const ContentBlockEditor = ({ block, index, onUpdate, onRemove, onMove, onOpenImageSelector }) => {
    const renderSizeSelector = () => {
        const options = block.type === 'text' ? [ { value: 'normal', label: 'Normale' }, { value: 'large', label: 'Grande' }, { value: 'medium', label: 'Medio' }, { value: 'small', label: 'Piccolo' } ] : [ { value: 'full', label: 'Piena Larghezza' }, { value: 'large', label: 'Grande (75%)' }, { value: 'medium', label: 'Media (50%)' }, { value: 'small', label: 'Piccola (30%)' } ];
        return ( <select className="block-size-selector" value={block.size || (block.type === 'text' ? 'normal' : 'full')} onChange={(e) => onUpdate(index, 'size', e.target.value)} title="Dimensione Blocco">{options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select> );
    };
    const handleTextBlockChange = (field, lang, value) => { const newTextValue = { ...(block.value || {}), [lang]: value }; onUpdate(index, 'value', newTextValue); };
    if (block.type === 'text') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Testo</span>{renderSizeSelector()}<div className="block-actions"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div><MultilangInput label="" field="value" values={block.value || {}} onChange={handleTextBlockChange} isTextarea rows={6} /></div> ); }
    if (block.type === 'image') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Immagine</span>{renderSizeSelector()}</div><div className="image-block-input-group"><input type="text" value={block.value} onChange={(e) => onUpdate(index, 'value', e.target.value)} placeholder="URL immagine (incolla o scegli)" className="image-url-input"/><button type="button" onClick={() => onOpenImageSelector(index)} className="choose-image-btn">Scegli</button></div>{block.value && <img src={block.value} alt="anteprima" className="block-image-preview"/>}<div className="block-actions-bottom"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div> ); }
    return null;
};

// --- Componente Form per Servizi ---
const ServiceForm = ({ initialData = {}, onSubmit, onCancel, isSaving }) => {
    const [title, setTitle] = useState(initialData.title || {});
    const [description, setDescription] = useState(initialData.description || {});
    const [imageUrl, setImageUrl] = useState(initialData.imageUrl || '');
    const [order, setOrder] = useState(initialData.order || '');
    const [detailContent, setDetailContent] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [targetBlockIndex, setTargetBlockIndex] = useState(null);

    useEffect(() => {
        setTitle(initialData.title || {});
        setDescription(initialData.description || {});
        setImageUrl(initialData.imageUrl || '');
        setOrder(initialData.order || '');
        const contentWithIds = (initialData.detailContent || []).map(b => ({...b, _id: Math.random().toString(36).substr(2, 9)}));
        setDetailContent(contentWithIds);
        setUploadedImages([]);
    }, [initialData]);

    const handleTextChange = (field, lang, value) => { const setter = { title: setTitle, description: setDescription }[field]; if (setter) { setter(prev => ({ ...prev, [lang]: value })); } };
    const handleSubmit = (e) => { e.preventDefault(); const contentToSave = detailContent.map(({ _id, ...rest }) => rest); onSubmit({ title, description, imageUrl, order: order ? parseInt(order, 10) : 999, detailContent: contentToSave }); };
    const addContentBlock = (type) => setDetailContent([...detailContent, { type, value: type === 'text' ? {} : '', size: type === 'text' ? 'normal' : 'full', _id: Math.random().toString(36).substr(2, 9)}]);
    const updateContentBlock = (index, field, value) => { const nc = [...detailContent]; nc[index] = { ...nc[index], [field]: value }; setDetailContent(nc); };
    const removeContentBlock = (index) => { if (window.confirm("Rimuovere blocco?")) setDetailContent(detailContent.filter((_, i) => i !== index)); };
    const moveContentBlock = (index, dir) => { const ni = index + dir; if (ni < 0 || ni >= detailContent.length) return; const nc = [...detailContent]; [nc[index], nc[ni]] = [nc[ni], nc[index]]; setDetailContent(nc); };
    const openImageSelector = (index) => { setTargetBlockIndex(index); setIsSelectorOpen(true); };
    const handleImageSelect = (url) => { if (targetBlockIndex !== null) { updateContentBlock(targetBlockIndex, 'value', url); } setIsSelectorOpen(false); setTargetBlockIndex(null); };

    return (
        <>
            {isSelectorOpen && (<ImageSelectorModal uploadedImages={uploadedImages} onSelect={handleImageSelect} onCancel={() => setIsSelectorOpen(false)}/>)}
            <div className="service-form-container">
                <form onSubmit={handleSubmit}>
                    <h4>{initialData.id ? 'Modifica Servizio' : 'Aggiungi Nuovo Servizio'}</h4>
                    <h5>Informazioni Principali</h5>
                    <MultilangInput label="Titolo Servizio*" field="title" values={title} onChange={handleTextChange} />
                    <MultilangInput label="Descrizione (Lista)*" field="description" values={description} onChange={handleTextChange} isTextarea />
                    <div className="form-group"><label>Ordine Visualizzazione</label><input type="number" value={order} onChange={(e) => setOrder(e.target.value)} disabled={isSaving}/></div>
                    <div className="form-group"><label>Immagine Principale*</label><ImageUploader currentImageUrl={imageUrl} onUploadComplete={setImageUrl} folderPath="services/main" /></div>
                    <hr className="form-divider" />
                    <h5>Contenuti Pagina Dettaglio</h5>
                    <div className="content-blocks-container">
                        {detailContent.map((block, index) => (<ContentBlockEditor key={block._id} index={index} block={block} onUpdate={updateContentBlock} onRemove={removeContentBlock} onMove={moveContentBlock} onOpenImageSelector={openImageSelector}/>))}
                    </div>
                    <div className="add-block-buttons">
                        <button type="button" onClick={() => addContentBlock('text')}>+ Aggiungi Testo</button>
                        <button type="button" onClick={() => addContentBlock('image')}>+ Aggiungi Immagine (Vuoto)</button>
                    </div>
                    <hr className="form-divider" />
                    <h5>Galleria Immagini (per questo servizio)</h5>
                    <h5>Galleria Immagini</h5>
                    <p className="form-instructions">Carica qui tutte le immagini... (Puoi selezionarne più di una tenendo premuto Ctrl o Cmd).</p>
                    <div className="temporary-image-gallery">
                        <ImageUploader 
                            currentImageUrl="" 
                            onUploadComplete={(url) => setUploadedImages(prev => [...prev, url])} 
                            folderPath="rooms/details_temp" 
                            multiple={true} /* <---- AGGIUNGI QUESTA PROPRIETÀ ----> */
                        />
                        <div className="uploaded-previews">
                            {uploadedImages.map((url, i) => <img key={i} src={url} alt={`caricata ${i+1}`} />)}
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="cta-button" disabled={isSaving || !imageUrl}>Salva Servizio</button>
                        <button type="button" className="cancel-button" onClick={onCancel} disabled={isSaving}>Annulla</button>
                    </div>
                </form>
            </div>
        </>
    );
};

// --- Componente Card Servizio ---
const ServiceCard = ({ service, onEdit, onDelete }) => {
    const { i18n } = useTranslation();
    const { currentUser } = useAuth();
    const lang = i18n.language;
    const fallbackLang = 'it';
    const title = service.title?.[lang] || service.title?.[fallbackLang] || 'Titolo non disponibile';
    const description = service.description?.[lang] || service.description?.[fallbackLang] || 'Descrizione...';
    const { t } = useTranslation();
    return (
        <div className="card service-item">
             <img src={service.imageUrl || '/placeholder-image.jpg'} alt={title} className="card-img" onError={(e) => { e.target.onerror = null; e.target.src='/placeholder-image.jpg'}}/>
             <div className="card-body">
                 <h3>{title}</h3>
                 <p>{description}</p>
                 <Link to={`/servizi/${service.id}`} className="cta-button details-button">{t('buttonDiscoverMore')}</Link>
             </div>
              {currentUser && (
                <div className="admin-card-controls">
                     <button onClick={() => onEdit(service)} title="Modifica Servizio">✏️</button>
                     <button onClick={() => onDelete(service.id, title)} title="Elimina Servizio">🗑️</button>
                </div>
              )}
         </div>
    );
}

// --- Componente Pagina Principale ---
const ServicesPage = () => {
    const [services, setServices] = useState([]);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const servicesQuery = query(collection(db, 'services'), orderBy('order', 'asc'), orderBy('title.it', 'asc'));
        const unsubscribe = onSnapshot(servicesQuery, (snapshot) => { setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); }, (err) => { setError("Impossibile caricare i servizi."); });
        return () => unsubscribe();
    }, []);

    const handleFormSubmit = async (formData) => {
        setIsSaving(true);
        setError(null);
        const dataToSave = { ...formData, lastUpdatedBy: currentUser?.email, updatedAt: serverTimestamp() };
        try {
            if (editingService?.id) {
                await setDoc(doc(db, 'services', editingService.id), dataToSave, { merge: true });
            } else {
                dataToSave.createdAt = serverTimestamp();
                await addDoc(collection(db, 'services'), dataToSave);
            }
            setShowForm(false);
            setEditingService(null);
        } catch (err) {
            setError("Errore durante il salvataggio.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleDeleteService = async (id, title) => {
        if (window.confirm(`Sei sicuro di voler eliminare il servizio "${title}"?`)) {
            try { await deleteDoc(doc(db, 'services', id)); alert(`Servizio "${title}" eliminato.`); }
            catch (err) { alert("Errore durante l'eliminazione."); }
        }
    };
    
    const handleAddClick = () => { setEditingService(null); setShowForm(true); };
    const handleEditClick = (service) => { setEditingService(service); setShowForm(true); };
    const handleFormCancel = () => { setShowForm(false); setEditingService(null); };

    return (
                    <div className="services-page">
                <LoadingSpinner show={isSaving} />
                <section className="page-header">
                    <div className="container text-center">
                        <EditableContentMultilang docPath="services_page/header" field="title" elementType="h1" />
                        <EditableContentMultilang docPath="services_page/header" field="intro_text" elementType="p" />
                        {currentUser && !showForm && (<button onClick={handleAddClick} className="cta-button add-service-button">+ Aggiungi Servizio</button>)}
                    </div>
                </section>
                {showForm && currentUser && (
                    <section className="content-section add-service-form-section">
                        <div className="container">
                            <ServiceForm initialData={editingService || {}} onSubmit={handleFormSubmit} onCancel={handleFormCancel} isSaving={isSaving}/>
                            {error && !isSaving && <p className="form-status error">{error}</p>}
                        </div>
                    </section>
                )}
                <section className="content-section">
                    <div className="container">
                        {error && !showForm && <p className="form-status error">{error}</p>}
                        {!error && services.length === 0 && (<p className="no-services-text">Nessun servizio attualmente inserito.</p>)}
                        {services.length > 0 && (
                            <div className="services-list-grid">
                                {services.map(service => (
                                    <ServiceCard key={service.id} service={service} onEdit={handleEditClick} onDelete={handleDeleteService}/>
                                ))}
                            </div>
                        )}
                        <div className="services-notes-section">
                            <EditableContentMultilang docPath="services_page/notes" field="additional_info_title" elementType="h3" />
                            <EditableContentMultilang docPath="services_page/notes" field="additional_info_text" elementType="p" />
                        </div>
                    </div>
                </section>
            </div>
    );
};

export default ServicesPage;

